// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0

import type { AgentCoreConfig, AgentPattern, ChunkParser, ImageAttachment, StreamCallback, UploadReference } from "./types"
import { parseStrandsChunk } from "./parsers/strands"
import { parseOcesaChunk } from "./parsers/ocesa"
import { parseLanggraphChunk } from "./parsers/langgraph"
import { parseClaudeAgentSdkChunk } from "./parsers/claude-agent-sdk"
import { parseAguiChunk } from "./parsers/agui"
import { readSSEStream } from "./utils/sse"
import { LOCAL_AGENT, LOCAL_MOCK_JWT } from "@/lib/localDev"

/** Resolve parser from pattern prefix. Defaults to strands parser. */
function getParser(pattern: AgentPattern): ChunkParser {
  if (pattern.startsWith("agui-")) return parseAguiChunk
  if (pattern.startsWith("langgraph-")) return parseLanggraphChunk
  if (pattern.startsWith("claude-")) return parseClaudeAgentSdkChunk
  if (pattern.startsWith("ocesa-")) return parseOcesaChunk
  if (pattern.startsWith("strands-")) return parseStrandsChunk
  return parseStrandsChunk
}

export class AgentCoreClient {
  private pattern: AgentPattern
  private parser: ChunkParser
  private agentApiUrl?: string

  constructor(config: AgentCoreConfig) {
    this.pattern = config.pattern
    this.parser = getParser(config.pattern)
    this.agentApiUrl = config.agentApiUrl
  }

  generateSessionId(): string {
    return crypto.randomUUID()
  }

  async invoke(
    query: string,
    sessionId: string,
    accessToken: string,
    onEvent: StreamCallback,
    signal?: AbortSignal,
    modelId?: string,
    effort?: string,
    images?: ImageAttachment[],
    uploads?: UploadReference[]
  ): Promise<void> {
    // Local mode (VITE_AGENT_LOCAL=1): post to a relative path that the Vite
    // dev server proxies to the local agent shim on :8080. No deployed runtime
    // ARN and no real Cognito token are required — the local shim does not
    // validate the JWT, so a throwaway unsigned token stands in for one.
    // Antes acá había un fallback a la URL del data plane de AgentCore, armada con el
    // `runtimeArn`. Era inalcanzable por construcción: el runtime no tiene authorizer
    // JWT de cara al navegador, así que el browser no puede invocarlo — lo invoca el
    // Lambda de /chat con credenciales IAM. Mantenerlo obligaba a publicar el ARN del
    // runtime (y con él el account id) en `aws-exports.json`, que es de lectura
    // pública, para alimentar un camino que nunca podía funcionar.
    //
    // La URL se resuelve DENTRO de cada rama, no con un ternario después: así el
    // chequeo de `agentApiUrl` la estrecha a `string` y no hace falta un `!`.
    let url: string
    if (LOCAL_AGENT) {
      accessToken = accessToken || LOCAL_MOCK_JWT
      url = "/invocations"
    } else {
      if (!accessToken) throw new Error("No valid access token found.")
      if (!this.agentApiUrl) throw new Error("Agent endpoint not configured.")
      url = this.agentApiUrl
    }

    const traceId = `1-${Math.floor(Date.now() / 1000).toString(16)}-${crypto.randomUUID()}`

    // Build payload based on pattern — AG-UI protocol expects a different format
    const body = this.pattern.startsWith("agui-")
      ? {
          threadId: sessionId,
          runId: crypto.randomUUID(),
          messages: [{ id: crypto.randomUUID(), role: "user", content: query }],
          state: {},
          tools: [],
          context: [],
          forwardedProps: {},
        }
      : {
          // OCESA promotores agent reads the prompt at input.prompt (app.py
          // @app.entrypoint). Nest it here so the same body works local + deployed.
          input: { prompt: query },
          runtimeSessionId: sessionId,
          // The API Gateway /chat Lambda reads body.session_id (snake_case) for
          // the runtime session; harmless on the direct-AgentCore path.
          session_id: sessionId,
          ...(modelId ? { modelId } : {}),
          // Omitted when falsy so a default-effort turn sends no effort and
          // stays a pure cache hit (the caller passes undefined at the default).
          ...(effort ? { effort } : {}),
          // Attached images (multi-modal vision) — omitted when none so a
          // text-only turn's payload is unchanged. The runtime validates the
          // format allow-list + size ceiling before the model sees them.
          ...(images && images.length ? { images } : {}),
          // Data-file uploads (fenced external lane), referenced by S3 key.
          // Omitted when none. The runtime downloads + fences them as u* handles.
          ...(uploads && uploads.length ? { uploads } : {}),
        }

    // User identity is extracted server-side from the validated JWT token
    // (Authorization header), not sent in the payload body. This prevents
    // impersonation via prompt injection.
    // On the API Gateway /chat path (agentApiUrl) send only standard headers:
    // the AgentCore-specific headers aren't in that route's CORS allow-list
    // (preflight would reject them) and the chat Lambda reads session_id from
    // the body.
    //
    // El `if` de abajo ahora sólo se cumple en modo local: fuera de local
    // `agentApiUrl` es obligatorio (si falta, se tira más arriba). Antes cubría además
    // la llamada directa al data plane de AgentCore, que ya no existe. Se conserva
    // porque el shim local ES la app de AgentCore y lee esos headers.
    const headers: Record<string, string> = {
      Authorization: `Bearer ${accessToken}`,
      "Content-Type": "application/json",
    }
    if (!this.agentApiUrl) {
      headers["X-Amzn-Trace-Id"] = traceId
      headers["X-Amzn-Bedrock-AgentCore-Runtime-Session-Id"] = sessionId
    }

    const response = await fetch(url, {
      method: "POST",
      headers,
      body: JSON.stringify(body),
      // Lets the UI cancel a long-running turn; aborting rejects the in-flight
      // fetch / stream read, which we treat as a clean stop below.
      signal,
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`HTTP ${response.status}: ${errorText}`)
    }

    try {
      await readSSEStream(response, this.parser, onEvent)
    } catch (err) {
      // A user-initiated abort is a normal stop, not an error — swallow it so
      // callers don't surface an error banner for "Detener".
      if (signal?.aborted || (err as Error)?.name === "AbortError") {
        return
      }
      throw err
    }
  }
}
